import { getWorkspaceId } from '@/entities/workspace';
import { useChatStore } from '@/features/chats/stores';
import { pickPersistableSettings, saveSettings } from './chatSettingsStorage';

/** Registry id of the templates agent (see ChatOptions.registry.ts). */
// eslint-disable-next-line i18next/no-literal-string -- agent registry id
const TEMPLATES_AGENT_ID = 'templates';

/**
 * Persists the templates-agent chat settings snapshot from the CURRENT store state. Call this after
 * any change that reflects real user INTENT: a control's `onChange`, or the model-compatibility
 * guard's auto-switch (user-derived — it is caused by the user attaching a video).
 *
 * Never call it from a component DEFAULT/INIT re-seed effect. Those re-seeds fire after
 * `setAgentById` wipes `chatInput.options` and carry no user intent, so a save there would clobber
 * stored settings with component defaults. Because they don't go through the user handlers, they
 * never reach this function — that's the invariant to preserve. "Not from an effect" is the wrong
 * way to state it; what matters is not from a *default/init* effect.
 *
 * The compatibility guard MUST persist. `useChatSettingsPersistence` treats storage as
 * authoritative: its store←storage sync depends on `options`, so a store-only model change wakes it
 * and `target.llmOptions.model` overrides the switched value straight back to the stored one. That
 * leaves either a guard/sync ping-pong, or a "model switched to X" alert next to a picker still
 * showing the old model and a send button blocked by `videoModelIncompatible`.
 *
 * Consequence to be aware of: a switch triggered by attaching a video outlives the message that
 * caused it (it also seeds the `ws:` default for new chats). That is intentional — the picker
 * genuinely shows the new model and the alert told the user. If it should ever become
 * session-scoped, do it by passing `workspaceId: undefined` for guard-driven saves, NOT by skipping
 * the save.
 *
 * Reads the store imperatively: Zustand `set` is synchronous, so by the time a handler calls this
 * (after its setter) `chatInput.options` already holds the new value in canonical shape
 * (`llmOptions.model`, etc.). No-op unless the templates agent is active.
 */
export function persistTemplatesSettingsFromStore(): void {
  const state = useChatStore.getState();
  if (state.currentAgentId !== TEMPLATES_AGENT_ID) return;
  const settings = pickPersistableSettings(state.chatInput.options);
  saveSettings(settings, {
    chatId: state.chatId || undefined,
    workspaceId: getWorkspaceId() || undefined,
  });
}
